/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik, FormikProvider, FieldArray} from 'formik'

import {KTCard, KTCardBody, KTCardHeader, Select, Loading, Datepicker, HelpIcon} from '../../../components'
import {PageTitle} from '../../../core'
import {KTSVG} from '../../../helpers'
import {useDispatch, useSelector} from 'react-redux'
import {
  allCompanyLookup,
  basicLookup,
  allProductLookup,
  warehouseLookup,
  projectLookup,
  districtLookup,
} from '../../../redux/slices/lookup.slice'
import {incomingWaybillCreate} from '../../../redux/slices/product.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  productLookup: state.lookup.productLookup,
  warehouseLookupList: state.lookup.warehouseLookupList,
  companyLookup: state.lookup.companyLookup,
  projectLookupList: state.lookup.projectLookupList,
  ShipmentType: state.lookup.ShipmentType,
  Province: state.lookup.Province,
  District: state.lookup.District,
  loading: state.lookup.loading,
})

const selectProductData = (state) => ({
  isRedirect: state.product.isRedirect,
  loading: state.product.loading,
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const [shipmentDistrict, setShipmentDistrict] = useState()
  const [exitDistrict, setExitDistrict] = useState()
  const [districtType, setDistrictType] = useState()
  const {
    productLookup,
    warehouseLookupList,
    companyLookup,
    projectLookupList,
    ShipmentType,
    Province,
    District,
    loading,
  } = useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectProductData)
  const dispatch = useDispatch()

  useEffect(() => {
    districtType === 'Shipment' ? setShipmentDistrict(District) : setExitDistrict(District)
  }, [District, districtType])

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(basicLookup('ShipmentType'))
    dispatch(basicLookup('Province'))
    dispatch(allProductLookup())
    dispatch(warehouseLookup())
    dispatch(projectLookup())
  }, [dispatch])

  const onChangeShipmentDistrict = (value) => {
    formik.setFieldValue('ShipmentProvinceId', value)
    dispatch(districtLookup({provinceId: value}))
    setDistrictType('Shipment')
  }

  const onChangeExitDistrict = (value) => {
    formik.setFieldValue('ExitProvinceId', value)
    dispatch(districtLookup({provinceId: value}))
    setDistrictType('Exit')
  }
  const [shipmentType, setShipmentType] = useState()

  const onChangeShipmentType = (value) => {
    formik.setFieldValue('ShipmentTypeId', value)
    setShipmentType(value)
  }

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Name: '',
    ProjectId: null,
    CompanyId: null,
    ShipmentAddress: '',
    ShipmentDistrictId: null,
    ShipmentProvinceId: null,
    ExitAddress: '',
    ExitDistrictId: null,
    ExitProvinceId: null,
    ShipmentTypeId: null,
    IssueDate: new Date(),
    ShipmentDate: new Date(),
    WaybillNumber: '',
    CarPlate: '',
    DriverName: '',
    CargoCompany: '',
    CargoCompanyTaxNumber: '',
    IncomingWaybillItems: [
      {
        ProductId: 0,
        WarehouseId: 0,
        Amount: 0,
      },
    ],
  })

  const onClickNewItem = useCallback((arrayHelpers) => {
    arrayHelpers.push({
      ProductId: '',
      WarehouseId: '',
      Amount: '',
    })
  }, [])

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(incomingWaybillCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.IN_WAYBILLS'}),
      path: '/stock/in-waybills',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  useEffect(() => {
    if (isRedirect && isRedirect.id) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
  }, [isRedirect, navigate, pageUrl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.IN_WAYBILLS.ADD'})}
      </PageTitle>
      <KTCard>
        <KTCardHeader title={intl.formatMessage({id: 'BC.IN_WAYBILLS.ADD'})}>
          <HelpIcon articleId='stock-inwaybill-create' />
        </KTCardHeader>
        <KTCardBody>
          <FormikProvider value={formik}>
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.PROJECT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={projectLookupList}
                    required
                    {...formik.getFieldProps('ProjectId')}
                    onChange={(item) => formik.setFieldValue('ProjectId', Number(item?.value))}
                  />
                  {formik.touched.ProjectId && formik.errors.ProjectId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ProjectId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.COMPANY'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={companyLookup}
                    {...formik.getFieldProps('CompanyId')}
                    onChange={(item) => formik.setFieldValue('CompanyId', Number(item?.value))}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.SHIPMENTADDRESS'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('ShipmentAddress')}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.SHIPMENTPROVINCE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={Province}
                    {...formik.getFieldProps('ShipmentProvinceId')}
                    onChange={(item) => onChangeShipmentDistrict(Number(item.value))}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.SHIPMENTDISTRICT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={shipmentDistrict}
                    {...formik.getFieldProps('ShipmentDistrictId')}
                    onChange={(item) =>
                      formik.setFieldValue('ShipmentDistrictId', Number(item?.value))
                    }
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.EXITADDRESS'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('ExitAddress')}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.EXITPROVINCE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={Province}
                    {...formik.getFieldProps('ExitProvinceId')}
                    onChange={(item) => onChangeExitDistrict(Number(item.value))}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.EXITDISTRICT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={exitDistrict}
                    {...formik.getFieldProps('ExitDistrictId')}
                    onChange={(item) => formik.setFieldValue('ExitDistrictId', Number(item?.value))}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.SHIPMENTTYPE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={ShipmentType}
                    {...formik.getFieldProps('ShipmentTypeId')}
                    onChange={(item) => onChangeShipmentType(Number(item.value))}
                  />
                </div>
              </div>
              {shipmentType == '1' && (
                <>
                  {' '}
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.CARPLATE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('CarPlate')}
                      />
                    </div>
                  </div>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.DRIVERNAME'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('DriverName')}
                      />
                    </div>
                  </div>
                </>
              )}
              {shipmentType == '2' && (
                <>
                  {' '}
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.CARGOCOMPANY'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('CargoCompany')}
                      />
                    </div>
                  </div>
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.CARGOCOMPANYTAXNUMBER'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <textarea
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('CargoCompanyTaxNumber')}
                      />
                    </div>
                  </div>
                </>
              )}
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.ISSUEDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('IssueDate')}
                    onChange={([date]) => formik.setFieldValue('IssueDate', date)}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.SHIPMENTDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('ShipmentDate')}
                    onChange={([date]) => formik.setFieldValue('ShipmentDate', date)}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'INCOMINGWAYBILL.CREATE.WAYBILLNUMBER'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('WaybillNumber')}
                  />
                </div>
              </div>

              <FieldArray
                name='IncomingWaybillItems'
                render={(arrayHelpers) => (
                  <>
                    <div className='table-responsive'>
                      <table className='table g-5 gs-0 mb-0 fw-bold text-gray-700'>
                        <thead>
                          <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                            <th>
                              {intl.formatMessage({
                                id: 'INCOMINGWAYBILL.CREATE.INCOMINGWAYBILLITEMS.PRODUCTID',
                              })}
                            </th>
                            <th>
                              {intl.formatMessage({
                                id: 'INCOMINGWAYBILL.CREATE.INCOMINGWAYBILLITEMS.WAREHOUSEID',
                              })}
                            </th>
                            <th>
                              {intl.formatMessage({
                                id: 'INCOMINGWAYBILL.CREATE.INCOMINGWAYBILLITEMS.AMOUNT',
                              })}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {formik.values.IncomingWaybillItems.map((item, index) => (
                            <tr className='border-bottom border-bottom-dashed' key={index}>
                              <td className='ps-1'>
                                <Select
                                  data={productLookup}
                                  name={`IncomingWaybillItems[${index}].ProductId`}
                                  value={formik.values.IncomingWaybillItems[index].ProductId}
                                  required
                                  onChange={(item) =>
                                    formik.setFieldValue(
                                      `IncomingWaybillItems[${index}].ProductId`,
                                      Number(item?.value)
                                    )
                                  }
                                />
                              </td>
                              <td className='ps-0'>
                                <Select
                                  data={warehouseLookupList}
                                  name={`IncomingWaybillItems[${index}].WarehouseId`}
                                  value={formik.values.IncomingWaybillItems[index].WarehouseId}
                                  required
                                  onChange={(item) =>
                                    formik.setFieldValue(
                                      `IncomingWaybillItems[${index}].WarehouseId`,
                                      Number(item?.value)
                                    )
                                  }
                                />
                              </td>
                              <td className='ps-0'>
                                <input
                                  type='number'
                                  className='form-control form-control-solid'
                                  autoComplete='off'
                                  disabled={formik.isSubmitting}
                                  placeholder={intl.formatMessage({
                                    id: 'INCOMINGWAYBILL.CREATE.INCOMINGWAYBILLITEMS.AMOUNT',
                                  })}
                                  name={`IncomingWaybillItems[${index}].Amount`}
                                  value={formik.values.IncomingWaybillItems[index].Amount}
                                  onChange={(event) => formik.handleChange(event)}
                                  onFocus={(event) => event.target.select()}
                                />
                              </td>

                              <td className='pt-5 text-end'>
                                <div className='d-flex'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-icon btn-active-color-primary'
                                    onClick={() => arrayHelpers.remove(index)}
                                  >
                                    <KTSVG
                                      path='/images/icons/duotune/general/gen027.svg'
                                      className='svg-icon-2'
                                    />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <button
                      type='button'
                      className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                      onClick={() => onClickNewItem(arrayHelpers)}
                    >
                      <KTSVG
                        path='/images/icons/duotune/arrows/arr009.svg'
                        className='svg-icon-2'
                      />
                      {intl.formatMessage({id: 'ADD_NEW_LINE'})}
                    </button>
                  </>
                )}
              />

              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </FormikProvider>
        </KTCardBody>
      </KTCard>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
